import { c as _c } from "react/compiler-runtime";
import { Children, isValidElement, useState } from 'react';
import type { ReactNode } from 'react';
import { SelectProps as MuiSelectProps } from '@mui/material/Select';
import MuiSelect from '@mui/material/Select';
import MuiFormControl from '@mui/material/FormControl';
import MuiLockIcon from '@mui/icons-material/Lock';
import { useMediaQuery } from '@mui/material';
import { useTheme } from '../styles';
import FormHelperText from '../FormHelperText';
import FormLabel from '../FormLabel';
import useFieldId from '../useFieldId';
import { getInputBaseRootStyle, getInputStyle } from '../TextField';
export interface Props<T = string> extends Omit<MuiSelectProps<T>, 'native' | 'placeholder' | 'variant'> {
  name: string;
  errorHelperText?: string;
  helperText?: string;
  sublabel?: string;
  noAsterisk?: boolean;
  isOptionalShown?: boolean;
  translations?: {
    optional: string;
  };
  hasError?: boolean;
  shouldDisableNativeOnMobile?: boolean;
  children: ReactNode;
}

/* istanbul ignore next */
const noop = () => {};
type OptionLikeChildProps = {
  children?: ReactNode;
  value?: string | number;
  disabled?: boolean;
};
function renderChildAsOption(child: ReactNode) {
  if (!isValidElement<OptionLikeChildProps>(child)) {
    return null;
  }
  const childProps = child.props;
  if (typeof childProps.children !== 'string' && child.type !== 'option') {
    // eslint-disable-next-line no-console
    console.warn('Cannot render child', child, 'as an option: its children are not a string.');
    return null;
  }
  return <option value={childProps.value} disabled={childProps.disabled}>
      {childProps.children}
    </option>;
}
function SelectField(t0) {
  const $ = _c(113);
  let children;
  let disabled;
  let id;
  let label;
  let name;
  let otherProps;
  let ref;
  let renderValue;
  let sublabel;
  let t1;
  let t10;
  let t11;
  let t12;
  let t2;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  let value;
  if ($[0] !== t0) {
    ({
      label,
      helperText: t1,
      disabled,
      hasError: t2,
      errorHelperText: t3,
      value,
      name,
      id,
      required: t4,
      noAsterisk: t5,
      isOptionalShown: t6,
      translations: t7,
      onChange: t8,
      onBlur: t9,
      onFocus: t10,
      shouldDisableNativeOnMobile: t11,
      children,
      sx: t12,
      renderValue,
      sublabel,
      ref,
      ...otherProps
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = disabled;
    $[3] = id;
    $[4] = label;
    $[5] = name;
    $[6] = otherProps;
    $[7] = ref;
    $[8] = renderValue;
    $[9] = sublabel;
    $[10] = t1;
    $[11] = t10;
    $[12] = t11;
    $[13] = t12;
    $[14] = t2;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
    $[19] = t7;
    $[20] = t8;
    $[21] = t9;
    $[22] = value;
  } else {
    children = $[1];
    disabled = $[2];
    id = $[3];
    label = $[4];
    name = $[5];
    otherProps = $[6];
    ref = $[7];
    renderValue = $[8];
    sublabel = $[9];
    t1 = $[10];
    t10 = $[11];
    t11 = $[12];
    t12 = $[13];
    t2 = $[14];
    t3 = $[15];
    t4 = $[16];
    t5 = $[17];
    t6 = $[18];
    t7 = $[19];
    t8 = $[20];
    t9 = $[21];
    value = $[22];
  }
  const helperText = t1 === undefined ? "" : t1;
  const hasError = t2 === undefined ? false : t2;
  const errorHelperText = t3 === undefined ? "" : t3;
  const required = t4 === undefined ? false : t4;
  const noAsterisk = t5 === undefined ? false : t5;
  const isOptionalShown = t6 === undefined ? false : t6;
  let t13;
  if ($[23] !== t7) {
    t13 = t7 === undefined ? {
      optional: "optional"
    } : t7;
    $[23] = t7;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  const translations = t13;
  const onChange = t8 === undefined ? noop : t8;
  const onBlur = t9 === undefined ? noop : t9;
  const onFocus = t10 === undefined ? noop : t10;
  const shouldDisableNativeOnMobile = t11 === undefined ? false : t11;
  let t14;
  if ($[25] !== t12) {
    t14 = t12 === undefined ? {} : t12;
    $[25] = t12;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  const sx = t14;
  const {
    breakpoints,
    spacing,
    palette
  } = useTheme();
  let t15;
  if ($[27] !== breakpoints) {
    t15 = breakpoints.up("sm");
    $[27] = breakpoints;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let shouldRenderNativeSelect = !useMediaQuery(t15);
  const [isFilled, setIsFilled] = useState(Boolean(value));
  const [isFocused, setIsFocused] = useState(false);
  let t16;
  if ($[29] !== spacing) {
    t16 = spacing(1.5, 1);
    $[29] = spacing;
    $[30] = t16;
  } else {
    t16 = $[30];
  }
  let t17;
  if ($[31] !== t16) {
    t17 = {
      padding: t16
    };
    $[31] = t16;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  let t18;
  if ($[33] !== palette) {
    t18 = getInputBaseRootStyle({
      palette
    });
    $[33] = palette;
    $[34] = t18;
  } else {
    t18 = $[34];
  }
  let t19;
  if ($[35] !== t18) {
    t19 = {
      ...t18
    };
    $[35] = t18;
    $[36] = t19;
  } else {
    t19 = $[36];
  }
  let t20;
  if ($[37] !== palette) {
    t20 = getInputStyle({
      palette
    });
    $[37] = palette;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== t17 || $[40] !== t19 || $[41] !== t20) {
    t21 = {
      "& .MuiSelect-select, & .MuiNativeSelect-select": t17,
      "&.MuiInputBase-root": t19,
      ...t20
    };
    $[39] = t17;
    $[40] = t19;
    $[41] = t20;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const selectStyles = t21;
  if (shouldDisableNativeOnMobile) {
    shouldRenderNativeSelect = false;
  }
  let endAdornment = null;
  if (disabled) {
    let t22;
    if ($[43] === Symbol.for("react.memo_cache_sentinel")) {
      t22 = <MuiLockIcon sx={{
        marginRight: "18px"
      }} />;
      $[43] = t22;
    } else {
      t22 = $[43];
    }
    endAdornment = t22;
  }
  const {
    labelId,
    fieldId
  } = useFieldId(name, id);
  let t22;
  if ($[44] !== errorHelperText || $[45] !== fieldId || $[46] !== hasError || $[47] !== helperText) {
    t22 = helperText || hasError && errorHelperText ? {
      "aria-describedby": `${fieldId}Helper`
    } : {};
    $[44] = errorHelperText;
    $[45] = fieldId;
    $[46] = hasError;
    $[47] = helperText;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  const ariaDescription = t22;
  let t23;
  if ($[49] !== sx) {
    t23 = {
      width: 1,
      ...sx
    };
    $[49] = sx;
    $[50] = t23;
  } else {
    t23 = $[50];
  }
  let t24;
  if ($[51] !== disabled || $[52] !== fieldId || $[53] !== hasError || $[54] !== isFilled || $[55] !== isFocused || $[56] !== isOptionalShown || $[57] !== label || $[58] !== labelId || $[59] !== noAsterisk || $[60] !== required || $[61] !== sublabel || $[62] !== translations) {
    t24 = label && <FormLabel error={hasError} focused={isFocused} disabled={disabled} filled={isFilled} required={required && !noAsterisk} id={labelId} htmlFor={fieldId} component="label" sublabel={sublabel} isOptionalShown={isOptionalShown && !required} translations={translations}>{label}</FormLabel>;
    $[51] = disabled;
    $[52] = fieldId;
    $[53] = hasError;
    $[54] = isFilled;
    $[55] = isFocused;
    $[56] = isOptionalShown;
    $[57] = label;
    $[58] = labelId;
    $[59] = noAsterisk;
    $[60] = required;
    $[61] = sublabel;
    $[62] = translations;
    $[63] = t24;
  } else {
    t24 = $[63];
  }
  const t25 = isFilled ? "form.field.input" : "form.field.placeholder";
  let t26;
  if ($[64] !== t25) {
    t26 = {
      color: t25
    };
    $[64] = t25;
    $[65] = t26;
  } else {
    t26 = $[65];
  }
  let t27;
  if ($[66] !== sx) {
    t27 = Array.isArray(sx) ? sx : [sx];
    $[66] = sx;
    $[67] = t27;
  } else {
    t27 = $[67];
  }
  let t28;
  if ($[68] !== selectStyles || $[69] !== t26 || $[70] !== t27) {
    t28 = [selectStyles, t26, ...t27];
    $[68] = selectStyles;
    $[69] = t26;
    $[70] = t27;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  let t29;
  if ($[72] === Symbol.for("react.memo_cache_sentinel")) {
    t29 = {
      anchorOrigin: {
        vertical: "bottom",
        horizontal: "left"
      },
      transformOrigin: {
        vertical: "top",
        horizontal: "left"
      }
    };
    $[72] = t29;
  } else {
    t29 = $[72];
  }
  let t30;
  if ($[73] !== onFocus) {
    t30 = e => {
      setIsFocused(true);
      onFocus(e);
    };
    $[73] = onFocus;
    $[74] = t30;
  } else {
    t30 = $[74];
  }
  let t31;
  if ($[75] !== onBlur) {
    t31 = e_0 => {
      setIsFocused(false);
      onBlur(e_0);
    };
    $[75] = onBlur;
    $[76] = t31;
  } else {
    t31 = $[76];
  }
  let t32;
  if ($[77] !== onChange) {
    t32 = (e_1, child) => {
      setIsFilled(Boolean(e_1.target.value));
      onChange(e_1, child);
    };
    $[77] = onChange;
    $[78] = t32;
  } else {
    t32 = $[78];
  }
  let t33;
  if ($[79] !== ariaDescription) {
    t33 = {
      ...ariaDescription
    };
    $[79] = ariaDescription;
    $[80] = t33;
  } else {
    t33 = $[80];
  }
  let t34;
  if ($[81] !== children || $[82] !== shouldRenderNativeSelect) {
    t34 = shouldRenderNativeSelect ? Children.map(children, renderChildAsOption) : children;
    $[81] = children;
    $[82] = shouldRenderNativeSelect;
    $[83] = t34;
  } else {
    t34 = $[83];
  }
  let t35;
  if ($[84] !== disabled || $[85] !== endAdornment || $[86] !== fieldId || $[87] !== hasError || $[88] !== labelId || $[89] !== name || $[90] !== otherProps || $[91] !== ref || $[92] !== renderValue || $[93] !== required || $[94] !== shouldRenderNativeSelect || $[95] !== t28 || $[96] !== t30 || $[97] !== t31 || $[98] !== t32 || $[99] !== t33 || $[100] !== t34 || $[101] !== value) {
    t35 = <MuiSelect variant="outlined" id={fieldId} labelId={labelId} name={name} data-testid="select" sx={t28} ref={ref} native={shouldRenderNativeSelect} disabled={disabled} error={hasError} value={value} required={required} MenuProps={t29} endAdornment={endAdornment} renderValue={renderValue} onFocus={t30} onBlur={t31} onChange={t32} inputProps={t33} {...otherProps}>{t34}</MuiSelect>;
    $[84] = disabled;
    $[85] = endAdornment;
    $[86] = fieldId;
    $[87] = hasError;
    $[88] = labelId;
    $[89] = name;
    $[90] = otherProps;
    $[91] = ref;
    $[92] = renderValue;
    $[93] = required;
    $[94] = shouldRenderNativeSelect;
    $[95] = t28;
    $[96] = t30;
    $[97] = t31;
    $[98] = t32;
    $[99] = t33;
    $[100] = t34;
    $[101] = value;
    $[102] = t35;
  } else {
    t35 = $[102];
  }
  const t36 = `${fieldId}Helper`;
  let t37;
  if ($[103] !== errorHelperText || $[104] !== hasError || $[105] !== helperText || $[106] !== t36) {
    t37 = <FormHelperText helperText={helperText} hasError={hasError} errorHelperText={errorHelperText} id={t36} />;
    $[103] = errorHelperText;
    $[104] = hasError;
    $[105] = helperText;
    $[106] = t36;
    $[107] = t37;
  } else {
    t37 = $[107];
  }
  let t38;
  if ($[108] !== t23 || $[109] !== t24 || $[110] !== t35 || $[111] !== t37) {
    t38 = <MuiFormControl sx={t23}>{t24}{t35}{t37}</MuiFormControl>;
    $[108] = t23;
    $[109] = t24;
    $[110] = t35;
    $[111] = t37;
    $[112] = t38;
  } else {
    t38 = $[112];
  }
  return t38;
}
export default SelectField;